import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useAuth } from '@shared/providers/AuthContext';
import {
  useMainResetPassword,
  useMainValidateResetToken,
} from '@/hooks/main/useMainAuth';
import { resetPasswordFormSchema, type ResetPasswordFormData } from '@/schemas/resetPasswordSchema';
import { AppButton, AppTextField } from '@/components/atoms';

/**
 * Reset Password Page Component
 *
 * Allows users to reset their password using a token from email.
 * Matches RA2 design; uses theme for button/link colors.
 */
const ResetPasswordPage: React.FC = () => {
  const { site, token } = useParams<{ site: string; token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { login } = useAuth();

  const validateResetTokenMutation = useMainValidateResetToken();
  const resetPasswordMutation = useMainResetPassword();

  const [isValidating, setIsValidating] = useState(true);
  const isLoading = resetPasswordMutation.isPending;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tokenValid, setTokenValid] = useState(false);

  const clinicNumberFromUrl = searchParams.get('clinic_number') || '';
  const loginIdFromUrl = searchParams.get('login_id') || '';
  const NO_MATCHING_USER_MESSAGE = '治療院ID、ユーザーIDが誤っている可能性があります。';
  const isNoMatchingUserMessage = (message?: string | null) => {
    if (!message) return false;
    return message.includes('治療院IDが見つかりません') || message.includes('ユーザIDが見つかりません');
  };
  const isExpiredTokenMessage = (message?: string | null) => {
    if (!message) return false;
    return message.includes('有効期限') && message.includes('切れ');
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useTrimmedForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: {
      clinic_number: clinicNumberFromUrl,
      login_id: loginIdFromUrl,
      password: '',
      password_confirmation: '',
    },
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token || !clinicNumberFromUrl || !loginIdFromUrl) {
        navigate(`/${site || 'medix'}/password/reset-expired`, { replace: true });
        return;
      }

      try {
        const response = await validateResetTokenMutation.mutateAsync({
          site: site || 'medix',
          token,
          clinicNumber: clinicNumberFromUrl,
          loginId: loginIdFromUrl,
        });

        if (response.success && response.data?.valid) {
          setTokenValid(true);
          setValue('clinic_number', clinicNumberFromUrl);
          setValue('login_id', loginIdFromUrl);
        } else {
          navigate(`/${site || 'medix'}/password/reset-expired`, { replace: true });
        }
      } catch {
        navigate(`/${site || 'medix'}/password/reset-expired`, { replace: true });
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, clinicNumberFromUrl, loginIdFromUrl, setValue]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    setError(null);

    try {
      const response = await resetPasswordMutation.mutateAsync({
        site: site || 'medix',
        token,
        clinicNumber: data.clinic_number,
        loginId: data.login_id,
        password: data.password,
        passwordConfirmation: data.password_confirmation,
      });

      if (response.success) {
        const loginResult = await login({
          clinic_number: data.clinic_number,
          login_id: data.login_id,
          password: data.password,
          site_type: site,
        });

        if (loginResult.success) {
          navigate(`/${site || 'medix'}`, {
            replace: true,
            state: { flashMessage: 'パスワードがリセットされました。' },
          });
          return;
        }

        setSuccess('パスワードを変更しました。');
        setError(loginResult.message || 'ログインに失敗しました。');
      } else {
        const message = response.message || 'エラーが発生しました。';
        if (isExpiredTokenMessage(message)) {
          navigate(`/${site || 'medix'}/password/reset-expired`, { replace: true });
          return;
        }
        setError(isNoMatchingUserMessage(message) ? NO_MATCHING_USER_MESSAGE : message);
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        const message = axiosError.response?.data?.message || 'エラーが発生しました。';
        if (isExpiredTokenMessage(message)) {
          navigate(`/${site || 'medix'}/password/reset-expired`, { replace: true });
          return;
        }
        setError(isNoMatchingUserMessage(message) ? NO_MATCHING_USER_MESSAGE : message);
      } else {
        setError('エラーが発生しました。');
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen login-page-bg">
      <div className="w-full px-4 max-w-[460px] mt-2.5">
        <div className="mb-3 text-center">
          <img
            src="/images/login/logo.png"
            alt="RA2N Logo"
            className="mx-auto w-[380px] max-w-full"
          />
        </div>

        <div className="px-8 py-6 bg-white rounded-lg shadow-lg bg-opacity-95">
          <h2 className="mb-4 text-lg font-semibold text-center text-gray-800">
            パスワードリセット
          </h2>

          {isValidating && (
            <div className="py-4 text-center">
              <p className="text-gray-600">トークンを確認中...</p>
            </div>
          )}

          {success && (
            <div
              className="px-4 py-3 mb-4 text-green-700 border border-green-200 rounded bg-green-50"
              role="alert"
              data-testid="success-message"
            >
              {success}
            </div>
          )}

          {error && !isValidating && (
            <div
              className="px-4 py-3 mb-4 text-red-700 border border-red-200 rounded bg-red-50"
              role="alert"
              data-testid="error-message"
            >
              {error}
            </div>
          )}

          {!isValidating && tokenValid && !success && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              <div>
                <Controller
                  name="clinic_number"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      id="clinic_number"
                      placeholder="治療院ID"
                      status={errors.clinic_number ? 'error' : undefined}
                      className="w-full"
                      data-testid="clinic-number-input"
                    />
                  )}
                />
                {errors.clinic_number && (
                  <p className="mt-1 text-sm text-red-500">{errors.clinic_number.message}</p>
                )}
              </div>

              <div>
                <Controller
                  name="login_id"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      id="login_id"
                      placeholder="ユーザーID"
                      status={errors.login_id ? 'error' : undefined}
                      className="w-full"
                      data-testid="login-id-input"
                    />
                  )}
                />
                {errors.login_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.login_id.message}</p>
                )}
              </div>

              <div>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      id="password"
                      placeholder="新しいパスワード"
                      status={errors.password ? 'error' : undefined}
                      className="w-full"
                      data-testid="password-input"
                    />
                  )}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              <div>
                <Controller
                  name="password_confirmation"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      id="password_confirmation"
                      type="password"
                      placeholder="新しいパスワード確認用"
                      status={errors.password_confirmation ? 'error' : undefined}
                      className="w-full"
                      data-testid="password-confirmation-input"
                    />
                  )}
                />
                {errors.password_confirmation && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.password_confirmation.message}
                  </p>
                )}
              </div>

              <div className="flex justify-center pt-3">
                <AppButton
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  disabled={isLoading}
                  className="px-8 py-2"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="submit-button"
                >
                  {isLoading ? '設定中...' : 'パスワードを設定する'}
                </AppButton>
              </div>
            </form>
          )}

          <div className="mt-4 text-center">
            <Link
              to={`/${site || 'medix'}/forgot-password`}
              className="text-sm hover:opacity-80"
              style={{ color: colors.primary }}
            >
              パスワードリセットに戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
